
import React, { useState, useEffect } from 'react';
import PageLayout from '../../components/PageLayout';
import Button from '../../components/ui/Button';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config';

const ManageVolunteers = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [volunteers, setVolunteers] = useState([]);
    const [loading, setLoading] = useState(true);

    const [applications, setApplications] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = user?.token;
                if (!token) return;

                const headers = { 'Authorization': `Bearer ${token} ` };

                // Fetch Users
                const usersRes = await fetch(`${API_BASE_URL}/users`, { headers });
                if (usersRes.ok) {
                    const data = await usersRes.json();
                    const mapped = data.map(u => ({
                        id: u._id,
                        name: u.name,
                        email: u.email,
                        hours: u.impactStats?.totalHours || 0,
                        events: u.impactStats?.completed || 0,
                        status: 'Active',
                        role: u.role
                    })).filter(u => u.role === 'volunteer');
                    setVolunteers(mapped);
                }

                // Fetch Applications
                const appsRes = await fetch(`${API_BASE_URL}/applications`, { headers });
                if (appsRes.ok) {
                    const appsData = await appsRes.json();
                    setApplications(appsData);
                }

            } catch (error) {
                console.error("Failed to fetch data", error);
            } finally {
                setLoading(false);
            }
        };

        if (user) {
            fetchData();
        }
    }, [user]);

    // ... (keep existing filteredVolunteers logic) ...
    const filteredVolunteers = volunteers.filter(volunteer =>
        volunteer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        volunteer.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleExport = () => {
        // ... (keep existing export logic for volunteers) ...
        const headers = ["Name,Email,Total Hours,Events Attended,Status"];
        const rows = filteredVolunteers.map(v =>
            `${v.name},${v.email},${v.hours},${v.events},${v.status}`
        );
        const csvContent = "data:text/csv;charset=utf-8," + [headers, ...rows].join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "volunteers_export.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleViewProfile = (name) => {
        alert(`Viewing profile for: ${name}\n(Feature coming soon!)`);
    };

    return (
        <PageLayout variant="dark">
            <div className="max-w-6xl mx-auto space-y-12">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/admin-portal')} className="text-slate-400 hover:text-white transition">
                        <Icon name="ArrowLeft" size={24} />
                    </button>
                    <div>
                        <h1 className="text-3xl font-bold text-white">Manage Volunteers & Applications</h1>
                        <p className="text-slate-400">Track performance and manage volunteer access</p>
                    </div>
                </div>

                {/* Volunteers Table */}
                <div className="bg-slate-800 rounded-xl shadow-lg border border-slate-700 overflow-hidden">
                    <div className="p-6 border-b border-slate-700 flex justify-between items-center">
                        <h2 className="text-xl font-bold text-white">Registered Volunteers</h2>

                        <div className="flex gap-4">
                            <div className="relative">
                                <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search volunteers..."
                                    className="bg-slate-700 border border-slate-600 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <Button variant="default" size="sm" iconName="Download" iconPosition="left" onClick={handleExport}>Export CSV</Button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-slate-300">
                            <thead className="bg-slate-900/50 uppercase text-xs font-semibold text-slate-400">
                                <tr>
                                    <th className="px-6 py-4">Name</th>
                                    <th className="px-6 py-4">Email</th>
                                    <th className="px-6 py-4">Total Hours</th>
                                    <th className="px-6 py-4">Events Attended</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700">
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-10 text-center text-slate-500">
                                            Loading volunteers...
                                        </td>
                                    </tr>
                                ) : filteredVolunteers.length > 0 ? (
                                    filteredVolunteers.map((volunteer) => (
                                        <tr key={volunteer.id} className="hover:bg-slate-700/50 transition">
                                            <td className="px-6 py-4 font-medium text-white">{volunteer.name}</td>
                                            <td className="px-6 py-4">{volunteer.email}</td>
                                            <td className="px-6 py-4">{volunteer.hours}</td>
                                            <td className="px-6 py-4">{volunteer.events}</td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${volunteer.status === 'Active' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                                                    }`}>
                                                    {volunteer.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <button
                                                    onClick={() => handleViewProfile(volunteer.name)}
                                                    className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                                                >
                                                    View Profile
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-10 text-center text-slate-500">
                                            No volunteers found matching "{searchTerm}"
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Applications Table */}
                <div className="bg-slate-800 rounded-xl shadow-lg border border-slate-700 overflow-hidden">
                    <div className="p-6 border-b border-slate-700">
                        <h2 className="text-xl font-bold text-white">Recent Applications</h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-slate-300">
                            <thead className="bg-slate-900/50 uppercase text-xs font-semibold text-slate-400">
                                <tr>
                                    <th className="px-6 py-4">Volunteer</th>
                                    <th className="px-6 py-4">Event</th>
                                    <th className="px-6 py-4">Organization</th>
                                    <th className="px-6 py-4">Date Applied</th>
                                    <th className="px-6 py-4">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700">
                                {applications.length > 0 ? (
                                    applications.map((app) => (
                                        <tr key={app._id} className="hover:bg-slate-700/50 transition">
                                            <td className="px-6 py-4 font-medium text-white">{app.user?.name || 'Unknown'}</td>
                                            <td className="px-6 py-4">{app.opportunity?.title || 'Unknown Event'}</td>
                                            <td className="px-6 py-4">{app.opportunity?.organization || 'N/A'}</td>
                                            <td className="px-6 py-4">{new Date(app.appliedAt).toLocaleDateString()}</td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${app.status === 'pending' ? 'bg-yellow-500/10 text-yellow-400' :
                                                    app.status === 'approved' ? 'bg-green-500/10 text-green-400' :
                                                        'bg-red-500/10 text-red-400'
                                                    }`}>
                                                    {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-10 text-center text-slate-500">
                                            No applications yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </PageLayout>
    );
};

export default ManageVolunteers;
